import { useCallback, useState } from 'react';
import PostalMime from 'postal-mime';
import { Form, Upload } from 'components';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import {
  useAttachmentFileBulkCreateMutation,
  useContactPersonReadQuery,
  useCreateLeadMutation,
  useUploadEmailMutation,
} from 'api';
import { CLIENT_EMAIL_ATTACHMENT_TYPE, EMAIL_ATTACHMENT_TYPE, MAX_EML_FILE_SIZE, MAX_FILE_SIZE } from 'constants/index';
import { useNotificationApi } from 'hooks/useNotificationApi';

const validAttachmentFormats = ['image/jpeg', 'image/png', 'text/csv', 'application/pdf'];

export const useAdd = () => {
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<any[]>([]);
  const [brandList, setBrandList] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<{ type: string; file: any }[]>([]);
  const [form] = Form.useForm();
  const { handleSuccess, handleError, handleWarning } = useNotificationMessages();
  const notificationApi = useNotificationApi();
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [selectedClient, setSelectedClient] = useState<string | undefined>(undefined);
  const [emailFile, setEmailFile] = useState<File | null>(null);
  const [emailPreview, setEmailPreview] = useState<{
    dateTime?: string;
    subject?: string;
    from?: string;
    body?: string;
  } | null>(null);

  const [createLead, { isLoading: createIsLoading }] = useCreateLeadMutation();
  const [uploadEmail, { isLoading: uploadIsLoading }] = useUploadEmailMutation();
  const [createBulkFiles] = useAttachmentFileBulkCreateMutation();
  const { data: contactPersons = [], isLoading: isLoadingContactPerson } = useContactPersonReadQuery(
    { entityId: selectedClient || '' },
    { skip: !selectedClient }
  );

  const navigate = useNavigate();

  const onCancel = useCallback(() => {
    form.resetFields();
    navigate(ADMIN_ROUTES.leads.list);
  }, [form, navigate]);

  const handleEmailUpload = useCallback(
    async (file: File) => {
      if (file.size > MAX_EML_FILE_SIZE) {
        form.setFieldValue('emailFile', null);
        setEmailPreview(null);
        form.setFields([
          {
            name: 'emailFile',
            errors: [`File exceeds the maximum size of 20 MB.`],
          },
        ]);
        setAttachments((oState) => oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE));
        notificationApi({ type: 'error', message: 'File exceeds the maximum size of 20 MB.' });
        return Upload.LIST_IGNORE;
      }
      const arrayBuffer = await file.arrayBuffer();
      const parser = new PostalMime();
      const parsedEmail = await parser.parse(arrayBuffer);

      // Create blob URLs for inline attachments (e.g., images)
      const cidMap: Record<string, string> = {};
      parsedEmail.attachments?.forEach((att: any) => {
        if (att.contentId && att.disposition === 'inline') {
          const blob = new Blob([att.content], { type: att.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          // Strip <> from contentId if present
          const cid = att.contentId.replace(/^<|>$/g, '');
          cidMap[cid] = blobUrl;
        }
      });

      // Replace cid: URLs in HTML with blob URLs
      let emailHtml = parsedEmail.html || `<pre>${parsedEmail.text}</pre>`;
      for (const [cid, url] of Object.entries(cidMap)) {
        const regex = new RegExp(`cid:${cid}`, 'g');
        emailHtml = emailHtml.replace(regex, url);
      }

      const files: File[] = parsedEmail.attachments
        .map((att) => {
          const file = new File([att.content], att.filename || 'attachment', {
            type: att.mimeType || 'application/octet-stream',
          });
          return file;
        })
        .filter((file) => {
          return validAttachmentFormats?.includes(file.type) && file.size <= MAX_FILE_SIZE;
        });

      if (parsedEmail.attachments.length > files.length) {
        handleWarning(
          'Attachments that exceed 5 GB or are in an unsupported file format are not added to the attachments section.'
        );
      }
      if (files.length) {
        setAttachments((oState) => [
          ...oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE),
          ...files.map((file) => ({ type: CLIENT_EMAIL_ATTACHMENT_TYPE, file: file })),
        ]);
      } else {
        setAttachments((oState) => oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE));
      }

      setEmailPreview({
        subject: parsedEmail.subject,
        from: parsedEmail.from.address,
        dateTime: parsedEmail.date,
        body: emailHtml,
      });
      setEmailFile(file);
      return false; // Prevent default upload behavior
    },
    [handleWarning, notificationApi, form]
  );

  const onFinish = async (values: any) => {
    const emailFormData = new FormData();
    emailFormData.append('file', emailFile as any);
    const emailResp = await uploadEmail(emailFormData);
    if (emailResp?.error) {
      handleError(emailResp?.error);
    }
    const emailId = emailResp?.data?.data;
    const payload = {
      emailId: emailId,
      clientId: values.clientId,
      contactPersonId: values.contactPersonId,
      clientRequestNumber: values.clientRequestNumber,
      source: values.source,
      brandIds: brandList,
      attachmentTypes: attachments.map((i) => i.type),
    };
    if (emailFile) {
      payload.attachmentTypes.push(EMAIL_ATTACHMENT_TYPE);
    }

    try {
      const { data, error }: any = await createLead(payload);
      if (data?.success) {
        const formData = new FormData();
        if (attachments?.length || data?.data?.length) {
          formData.append('attachmentIds', data.data as any);
          Array.from(attachments).forEach((attachment) => {
            formData.append('files', attachment.file);
          });
          if (emailFile) {
            formData.append('files', emailFile);
          }
          formData.append('serviceName', 'WRD');
          createBulkFiles({ data: formData, entityName: 'LEAD' });
          handleSuccess('The Lead was successfully added.');
          navigate(ADMIN_ROUTES.leads.list);
        } else {
          handleSuccess('The Lead was successfully added.');
          navigate(ADMIN_ROUTES.leads.list);
        }
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleFormChange = useCallback((values: Record<string, any>) => {
    setDisableSaveBtn(false);
    if (values.clientId) {
      setSelectedClient(values.clientId);
    }
  }, []);

  const handleDeleteBrand = useCallback(
    (index: number) => {
      const newBrand = [...brandList];
      newBrand.splice(index, 1);
      setBrandList(newBrand);
    },
    [brandList]
  );

  const onAddBrand = useCallback((brand: string) => {
    setBrandList((oState) => [...oState, brand]);
  }, []);

  return {
    onFinish,
    onCancel,
    attachments,
    setAttachments,
    form,
    disableSaveBtn,
    setDisableSaveBtn,
    fileList,
    setFileList,
    attachmentType,
    setAttachmentType,
    createIsLoading: createIsLoading || uploadIsLoading,
    handleFormChange,
    handleEmailUpload,
    emailPreview,
    setEmailPreview,
    emailFile,
    setEmailFile,
    brandList,
    setBrandList,
    onAddBrand,
    handleDeleteBrand,
    contactPersons,
    isLoadingContactPerson,
    selectedClient,
  };
};
